import React, { useCallback, useRef } from 'react';
import useTimeline from '@src/hooks/useTimeline';
import useLiveTime from '@src/hooks/useLiveTime';
import { StyledGrab, StyledLine, StyledPlayheadLine } from './index.style';

/**
 * The playhead's vertical line + grab strip, rendered INSIDE the scrollable
 * timeline content (alongside the ruler + lanes). Living in the same scroll
 * container as the clips means the browser scrolls and clips it natively: it
 * always lands exactly on its time, follows scroll with zero lag, and can never
 * spill into the track gutter. The time chip is drawn in PlayheadOverlay (above
 * the gutter, un-clipped) but is kept glued to this line by a synchronous
 * scroll sync.
 *
 * Its position is written to the DOM per animation frame (useLiveTime), not
 * animated by CSS, so playback is smooth despite the coarse time updates.
 *
 * The grab strip carries no handler of its own: a press on it reaches the
 * timeline's surface seek (see DubbingTimeline) by bubbling, so grabbing the
 * line scrubs like anywhere else — the strip only widens the line's hit area
 * and shows the ew-resize cursor.
 *
 * @param sec      playhead time (seconds), used while paused / scrubbing
 * @param playing  follow playback; off while scrubbing for 1:1 drag
 * @param getSec   reads the live playback position without a re-render
 */
const PlayheadLine = ({ sec = 0, playing = false, getSec }) => {
  const { secToPx } = useTimeline();
  const rootRef = useRef(null);

  // Re-created on zoom (secToPx changes), which re-places the line.
  const draw = useCallback(
    (t) => {
      if (rootRef.current) {
        rootRef.current.style.transform = `translateX(${secToPx(t)}px)`;
      }
    },
    [secToPx],
  );
  useLiveTime(sec, playing, draw, getSec);

  return (
    <StyledPlayheadLine ref={rootRef}>
      <StyledLine />
      <StyledGrab />
    </StyledPlayheadLine>
  );
};

export default PlayheadLine;
